import { useQuery } from '@tanstack/react-query'

import { api } from '../api'
import { formatDate, formatHM, formatMoney } from '../lib/format'
import { PAYOUT_STATUS_LABEL } from '../types'
import { useEscClose } from '../lib/useEscClose'

interface Props {
  payoutId: number
  onClose: () => void
}

/** Ведомость целиком: за какие задачи специалист получил деньги. */
export default function PayoutDialog({ payoutId, onClose }: Props) {
  const payout = useQuery({ queryKey: ['payout', payoutId], queryFn: () => api.payout(payoutId) })
  const p = payout.data

  useEscClose(onClose)

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal wide">
        <h2>
          {p ? `Ведомость · ${p.user_name}` : 'Ведомость'}
          {p && (
            <span className={`badge ${p.status}`} style={{ marginLeft: 10 }}>
              {PAYOUT_STATUS_LABEL[p.status]}
            </span>
          )}
        </h2>

        <div className="modal-body">
          {payout.isLoading && <div className="empty">Загрузка…</div>}

          {p && (
            <>
              <div className="payout-summary">
                <div>
                  <span className="payout-summary-label">Период</span>
                  <span className="payout-summary-value mono">
                    {formatDate(p.period_start)} — {formatDate(p.period_end)}
                  </span>
                </div>
                <div>
                  <span className="payout-summary-label">Оплачено часов</span>
                  <span className="payout-summary-value mono">
                    {p.items.reduce((sum, i) => sum + Number(i.hours), 0)} ч
                  </span>
                </div>
                <div>
                  <span className="payout-summary-label">Начислено</span>
                  <span className="payout-summary-value mono">{formatMoney(p.total_amount)}</span>
                </div>
                <div>
                  <span className="payout-summary-label">Зачтено в долг</span>
                  <span className="payout-summary-value mono">{formatMoney(p.debt_offset)}</span>
                </div>
                <div className="payout-summary-total">
                  <span className="payout-summary-label">На руки</span>
                  <span className="payout-summary-value mono">{formatMoney(p.paid_amount)}</span>
                </div>
              </div>

              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Дата</th>
                      <th>Клиент</th>
                      <th>Задача</th>
                      <th className="num">Факт</th>
                      <th className="num">Оплачено ч</th>
                      <th className="num">Ставка</th>
                      <th className="num">Сумма</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.items.length === 0 && (
                      <tr>
                        <td colSpan={7} className="empty">
                          В ведомости нет задач
                        </td>
                      </tr>
                    )}
                    {p.items.map((i) => (
                      <tr key={i.id}>
                        <td className="mono nowrap">{formatDate(i.work_date)}</td>
                        <td>
                          {i.client_name}
                          {i.source_name && <span className="cell-sub">{i.source_name}</span>}
                        </td>
                        <td className="cell-task">{i.task_title}</td>
                        <td className="num mono nowrap">{formatHM(i.seconds)}</td>
                        <td className="num">
                          <span className={i.by_estimate ? 'estimate-value' : 'mono'}>
                            {Number(i.hours)} ч
                          </span>
                        </td>
                        <td className="num mono nowrap">{formatMoney(i.rate)}</td>
                        <td className="num mono nowrap">{formatMoney(i.amount)}</td>
                      </tr>
                    ))}
                    {p.items.length > 0 && (
                      <tr style={{ fontWeight: 700 }}>
                        <td colSpan={4}>ИТОГО</td>
                        <td className="num mono">
                          {p.items.reduce((sum, i) => sum + Number(i.hours), 0)} ч
                        </td>
                        <td />
                        <td className="num mono">{formatMoney(p.total_amount)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <p style={{ color: 'var(--muted)' }}>
                Зелёной плашкой отмечены часы, взятые из оценки задачи. Без плашки — оплата
                по фактически отработанному времени.
              </p>

              {p.note && <p>{p.note}</p>}
            </>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  )
}
